import { enumerateBlockThemeMatrix } from '@web-studio/layout-matrix';
import { getColorSetsForTheme, THEME_REGISTRY } from '@web-studio/shared-schemas';
import type { LayoutComponentType } from '@web-studio/shared-schemas';
import { useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MatrixCanvas } from '@/components/MatrixCanvas';
import { MatrixToolbar } from '@/components/MatrixToolbar';
import {
  applyMatrixAxesToBlock,
  matrixStateToSearchParams,
  parseMatrixSearchParams,
  resolveFixtureBlocks,
} from '@/lib/matrixState';

type GridAxis = 'theme' | 'colorset';

export function GridComparePage() {
  const { componentType: routeType } = useParams<{ componentType: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const defaultType = (routeType as LayoutComponentType) ?? 'Hero_Editorial_Split';
  const state = useMemo(
    () => parseMatrixSearchParams(searchParams, defaultType),
    [searchParams, defaultType],
  );

  const axis: GridAxis = searchParams.get('axes') === 'colorset' ? 'colorset' : 'theme';
  const blocks = useMemo(() => {
    const raw = resolveFixtureBlocks(state.fixtureId);
    return raw.map((block) => applyMatrixAxesToBlock(block, state));
  }, [state]);

  const columns = useMemo(() => {
    if (axis === 'theme') {
      return Object.values(THEME_REGISTRY).map((t) => ({
        id: t.themeId,
        label: t.name,
        themeId: t.themeId,
        colorsetId: t.default_colorset_id,
      }));
    }
    return getColorSetsForTheme(state.themeId).map((c) => ({
      id: c.id,
      label: c.name,
      themeId: state.themeId,
      colorsetId: c.id,
    }));
  }, [axis, state.themeId]);

  const syncToolbar = (patch: Partial<typeof state>) => {
    const next = { ...state, ...patch };
    const p = matrixStateToSearchParams(next);
    p.set('axes', axis);
    navigate(`/matrix/grid/${next.componentType}?${p.toString()}`, { replace: true });
  };

  return (
    <div className="lab-page">
      <header className="lab-page-header">
        <h1 className="lab-page-header__title">Compare blocks</h1>
        <p className="lab-page-header__desc">
          Same arrangement · varying {axis === 'theme' ? 'themes (default colorset)' : 'colorsets'}
        </p>
      </header>

      <MatrixToolbar state={state} onChange={syncToolbar} />

      <div
        className="mt-4 grid gap-x-4 gap-y-8"
        style={{
          gridTemplateColumns: `repeat(${Math.min(columns.length, 4)}, minmax(260px, 1fr))`,
        }}
      >
        {columns.map((col) => (
          <div key={col.id} className="flex flex-col gap-2">
            <div className="flex items-center justify-center gap-3">
              <p className="text-xs font-medium" style={{ color: 'var(--lab-muted)' }}>
                {col.label}
              </p>
              <Link
                className="shrink-0 text-xs no-underline"
                style={{ color: 'var(--lab-accent)' }}
                to={`/matrix/block/${state.componentType}?${matrixStateToSearchParams({
                  ...state,
                  themeId: col.themeId,
                  colorsetId: col.colorsetId,
                }).toString()}`}
              >
                Open isolate
              </Link>
            </div>
            {blocks.length > 0 ? (
              <MatrixCanvas
                blocks={blocks}
                locale={state.locale}
                themeId={col.themeId}
                colorsetId={col.colorsetId}
                spacingEnum={state.spacingEnum}
                viewport={state.viewport}
                compact
              />
            ) : null}
          </div>
        ))}
      </div>

      <p className="text-xs" style={{ color: 'var(--lab-muted)' }}>
        Matrix cells for this slice:{' '}
        {
          enumerateBlockThemeMatrix({
            componentType: state.componentType,
            spacingEnum: state.spacingEnum,
            locale: state.locale,
            fixtureId: state.fixtureId,
            includeRegistryOnly: false,
          }).length
        }{' '}
        rendered combinations
      </p>
    </div>
  );
}
